import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { sSet } from '../lib/storage.js';
import { uid, todayStr } from '../lib/calculations.js';
import { useBusinessStore } from './businessStore';
import { classifyIncomeLabel } from '../lib/incomeClassifier.js';
import { useUserStore } from './userStore';

/* ══════════════════════════════════════════════════════
   FINANCE STORE — Transactions, budgets, income, etc.

   karatStorage wraps a string-based StateStorage with
   createJSONStorage so zustand's persist middleware
   stringifies StorageValue objects before writing.

   ⚠️ Bug history: A prior revision of this file removed the
   JSON.stringify from setItem, causing zustand to write the
   literal string "[object Object]" to localStorage on every
   state mutation. That broke persistence between reloads.
   This revision restores the correct contract via
   createJSONStorage and adds a corruption guard in getItem
   so users with the legacy corrupt string fall through to
   initial state + syncFromCloud rehydration on next auth.
   ══════════════════════════════════════════════════════ */

const karatStorage = createJSONStorage(() => ({
  getItem: (name) => {
    try {
      const raw = localStorage.getItem(name);
      if (raw == null) return null;
      // Corruption guard — "[object Object]" is the signature of the
      // pre-fix bug where setItem wrote an un-stringified object.
      // Return null so zustand uses initial state; syncFromCloud will
      // restore any cloud-persisted data on next authenticated load.
      if (raw === '[object Object]') {
        try { localStorage.removeItem(name); } catch {}
        return null;
      }
      return raw;
    } catch { return null; }
  },
  setItem: (name, value) => {
    // `value` here is already a JSON string thanks to createJSONStorage.
    try {
      localStorage.setItem(name, value);
    } catch { /* localStorage full or disabled */ }
    // Fire-and-forget cloud sync with the parsed object.
    try { sSet(name, JSON.parse(value)); } catch { /* sync best-effort */ }
  },
  removeItem: (name) => {
    try { localStorage.removeItem(name); } catch {}
  },
}));

const DEFAULT_BUDGET = {
  housing: 0, transport: 0, food: 0, health: 0,
  entertainment: 0, shopping: 0, savings: 0,
  subscriptions: 0, utilities: 0, other: 0,
};

const roundMoney = (value) => Math.round((parseFloat(value) || 0) * 100) / 100;

const personalReserveRate = (taxReserve) => {
  const rate = Number(taxReserve?.rate);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : 0.25;
};

const personalReserveTarget = (entry, rate) => (
  entry?.incomeType === '1099' && Number(entry?.amount) > 0
    ? roundMoney(Number(entry.amount) * rate)
    : 0
);

const sameLegacyPaydaySignature = (entry, candidate) => (
  entry?.incomeType === '1099'
  && candidate?.type === 'reserve'
  && !candidate?.sourceIncomeId
  && candidate?.source === `1099: ${entry.name || 'payday'}`
);

function reconcilePersonalPaydayReserve(state, previous, next) {
  const taxReserve = state.taxReserve || { balance: 0, rate: 0.25, transactions: [] };
  const rate = personalReserveRate(taxReserve);
  let transactions = [...(taxReserve.transactions || [])];

  const linked = transactions.filter(tx => tx.sourceIncomeId === previous.id);
  let previousTarget = Number.isFinite(Number(previous.taxReserveAmount))
    ? roundMoney(previous.taxReserveAmount)
    : roundMoney(linked.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0));

  // WHY: releases before this fix wrote a reserve with only the payday name.
  // Match those rows only when both sides are name-unique, or when amount and
  // creation order agree. That repairs a row already made stale by an old edit
  // without stealing another freelancer payment's reserve. Backfilled 1099
  // rows deliberately have no reserve and therefore do not match. New rows
  // are linked immutably by sourceIncomeId below.
  if (previousTarget === 0 && previous.incomeType === '1099') {
    const expected = personalReserveTarget(previous, rate);
    const legacyIndexes = transactions
      .map((tx, index) => ({ tx, index }))
      .filter(({ tx }) => sameLegacyPaydaySignature(previous, tx));
    const sameNamePaydays = (state.oneOffIncomes || []).filter(entry => (
      entry.incomeType === '1099'
      && entry.name === previous.name
    ));
    const amountMatchedLegacy = legacyIndexes.filter(
      ({ tx }) => roundMoney(tx.amount) === expected
    );
    const amountMatchedPaydays = sameNamePaydays.filter(entry => (
      roundMoney(entry.amount) === roundMoney(previous.amount)
    ));
    const ordinal = amountMatchedPaydays.findIndex(entry => entry.id === previous.id);
    const legacy = legacyIndexes.length === 1 && sameNamePaydays.length === 1
      ? legacyIndexes[0]
      : amountMatchedLegacy.length === 1
        ? amountMatchedLegacy[0]
        : amountMatchedLegacy[ordinal >= 0 ? ordinal : -1];
    if (legacy) {
      previousTarget = roundMoney(legacy.tx.amount);
      transactions[legacy.index] = {
        ...legacy.tx,
        sourceIncomeId: previous.id,
        reserveSourceKind: 'personal-1099',
      };
    }
  }

  const amountChanged = roundMoney(previous.amount) !== roundMoney(next?.amount);
  const typeChanged = previous.incomeType !== next?.incomeType;
  const target = !next
    ? 0
    : (!amountChanged && !typeChanged && next.incomeType === '1099'
      ? previousTarget
      : personalReserveTarget(next, rate));
  const requestedDelta = roundMoney(target - previousTarget);
  const balance = Math.max(0, roundMoney(taxReserve.balance));
  const appliedDelta = requestedDelta < 0
    ? -Math.min(balance, Math.abs(requestedDelta))
    : requestedDelta;

  if (appliedDelta !== 0) {
    const label = next?.name || previous.name || 'payday';
    transactions.push({
      id: uid(),
      amount: appliedDelta,
      source: next ? `1099 adjusted: ${label}` : `1099 removed: ${label}`,
      date: next?.date || todayStr(),
      type: 'adjustment',
      sourceIncomeId: previous.id,
      reserveSourceKind: 'personal-1099',
    });
  }

  return {
    nextEntry: next
      ? {
        ...next,
        taxReserveAmount: target,
        taxReserveRateApplied: next.incomeType === '1099'
          ? (amountChanged || typeChanged ? rate : previous.taxReserveRateApplied ?? rate)
          : null,
      }
      : null,
    taxReserve: {
      ...taxReserve,
      balance: roundMoney(balance + appliedDelta),
      transactions,
    },
  };
}

export const useFinanceStore = create(
  persist(
    (set, get) => ({
      // ── Transactions ──
      txns: {},

      addTransaction: (monthKey, txn) => set(state => {
        const existing = state.txns[monthKey] || [];
        return { txns: { ...state.txns, [monthKey]: [...existing, txn] } };
      }),

      updateTransaction: (monthKey, id, updates) => set(state => {
        const list = (state.txns[monthKey] || []).map(t =>
          t.id === id ? { ...t, ...updates } : t
        );
        return { txns: { ...state.txns, [monthKey]: list } };
      }),

      deleteTransaction: (monthKey, id) => set(state => {
        const list = (state.txns[monthKey] || []).filter(t => t.id !== id);
        return { txns: { ...state.txns, [monthKey]: list } };
      }),

      importTransactions: (monthKey, newTxns) => set(state => {
        const existing = state.txns[monthKey] || [];
        return { txns: { ...state.txns, [monthKey]: [...existing, ...newTxns] } };
      }),

      // ── Budgets ──
      budgets: {},

      setBudget: (monthKey, category, amount) => set(state => {
        const mb = state.budgets[monthKey] || { ...DEFAULT_BUDGET };
        return { budgets: { ...state.budgets, [monthKey]: { ...mb, [category]: amount } } };
      }),

      setBudgets: (monthKey, budgetObj) => set(state => ({
        budgets: { ...state.budgets, [monthKey]: budgetObj },
      })),

      // ── Income Sources ──
      incomes: [],
      setIncomes: (incomes) => set({ incomes }),
      addIncome: (source) => set(state => {
        // Detect "first income added" — if the user had zero income sources
        // before this call AND the new source has a positive amount AND there
        // is no strategy applied for the current month, flip the trigger flag
        // so the StrategyPickerModal mounted at App shell opens.
        const wasEmpty = state.incomes.length === 0;
        const hasAmount = parseFloat(source?.amount) > 0;
        const today = new Date();
        const mk = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        const monthBudget = state.budgets[mk];
        const strategyAlreadySet = !!monthBudget?._meta?.strategy;
        const shouldTrigger = wasEmpty && hasAmount && !strategyAlreadySet;
        return {
          incomes: [...state.incomes, source],
          ...(shouldTrigger ? { strategyPickerTrigger: 'first-income' } : {}),
        };
      }),
      updateIncome: (id, updates) => set(state => ({
        incomes: state.incomes.map(s => s.id === id ? { ...s, ...updates } : s),
      })),
      removeIncome: (id) => set(state => ({
        incomes: state.incomes.filter(s => s.id !== id),
      })),

      // ── Strategy Picker Trigger (transient — NOT persisted) ──
      // Flipped to 'first-income' by addIncome when income transitions 0 → positive
      // and the current month has no _meta.strategy. The StrategyPickerModal at
      // App shell level listens to this and opens. Also flippable from anywhere
      // ('manual') so a Dashboard hint or Switch-strategy button can re-open the
      // picker on demand. Cleared (set to null) when the modal closes.
      strategyPickerTrigger: null,
      setStrategyPickerTrigger: (strategyPickerTrigger) => set({ strategyPickerTrigger }),

      // ── Credit Cards ──
      cards: [],
      setCards: (cards) => set({ cards }),
      addCard: (card) => set(state => ({ cards: [...state.cards, card] })),
      updateCard: (id, updates) => set(state => ({
        cards: state.cards.map(c => c.id === id ? { ...c, ...updates } : c),
      })),
      removeCard: (id) => set(state => ({
        cards: state.cards.filter(c => c.id !== id),
      })),

      // ── Goals ──
      goals: [],
      setGoals: (goals) => set({ goals }),
      addGoal: (goal) => set(state => ({ goals: [...state.goals, goal] })),
      updateGoal: (id, updates) => set(state => ({
        goals: state.goals.map(g => g.id === id ? { ...g, ...updates } : g),
      })),
      removeGoal: (id) => set(state => ({
        goals: state.goals.filter(g => g.id !== id),
      })),
      addGoalContribution: (id, contribution) => set(state => ({
        goals: state.goals.map(g => g.id === id ? {
          ...g,
          current: Math.max(0, parseFloat(g.current) || 0)
            + Math.max(0, parseFloat(contribution.amount) || 0),
          contributions: [
            ...(g.contributions || []),
            { ...contribution, amount: Math.max(0, parseFloat(contribution.amount) || 0) },
          ],
        } : g),
      })),

      // ── Recurring Bills ──
      recurring: [],
      setRecurring: (recurring) => set({ recurring }),
      addRecurring: (bill) => set(state => ({ recurring: [...state.recurring, bill] })),
      updateRecurring: (id, updates) => set(state => ({
        recurring: state.recurring.map(r => r.id === id ? { ...r, ...updates } : r),
      })),
      removeRecurring: (id) => set(state => ({
        recurring: state.recurring.filter(r => r.id !== id),
      })),

      // ── Equity Compensation (RSU / ISO / NSO / ESPP) ──
      // Each grant: { id, ticker, company, type, grantDate, sharesTotal, strikePrice,
      //   currentPrice, vestSchedule: [{date, shares}], supplementalRate }
      equityGrants: [],
      addEquityGrant: (g) => set(state => ({ equityGrants: [...state.equityGrants, g] })),
      updateEquityGrant: (id, updates) => set(state => ({
        equityGrants: state.equityGrants.map(x => x.id === id ? { ...x, ...updates } : x),
      })),
      removeEquityGrant: (id) => set(state => ({
        equityGrants: state.equityGrants.filter(x => x.id !== id),
      })),

      // ── Calendar Activities (one-off projected costs) ──
      activities: [],
      addActivity: (a) => set(state => ({ activities: [...state.activities, a] })),
      updateActivity: (id, updates) => set(state => ({
        activities: state.activities.map(x => x.id === id ? { ...x, ...updates } : x),
      })),
      removeActivity: (id) => set(state => ({
        activities: state.activities.filter(x => x.id !== id),
      })),

      // ── One-off Paydays (separate from incomes[] schedules) ──
      oneOffIncomes: [],
      addOneOffIncome: (p) => set(state => ({ oneOffIncomes: [...state.oneOffIncomes, p] })),
      updateOneOffIncome: (id, updates) => set(state => {
        const previous = state.oneOffIncomes.find(x => x.id === id);
        if (!previous) return {};
        const candidate = { ...previous, ...updates };
        const reconciled = reconcilePersonalPaydayReserve(state, previous, candidate);
        return {
          oneOffIncomes: state.oneOffIncomes.map(x =>
            x.id === id ? reconciled.nextEntry : x
          ),
          taxReserve: reconciled.taxReserve,
        };
      }),
      removeOneOffIncome: (id) => set(state => {
        const previous = state.oneOffIncomes.find(x => x.id === id);
        if (!previous) return {};
        const reconciled = reconcilePersonalPaydayReserve(state, previous, null);
        return {
          oneOffIncomes: state.oneOffIncomes.filter(x => x.id !== id),
          taxReserve: reconciled.taxReserve,
        };
      }),

      // ── 1099 tax set-aside (personal tier) ──
      // Single entry point for personal-tier one-off payday logs.
      // Mirrors businessStore.logBizIncome: wraps the pure setter,
      // routes a tax slice to financeStore.taxReserve when the
      // entry is flagged '1099', and returns { amount, reserved,
      // incomeType } so the UI can render a toast.
      //
      // Business Mode gate (defense-in-depth): if bizSettings.enabled
      // is true, forces incomeType to 'w2' so no reserve fires even
      // if a stale UI somehow submits '1099'. The dropdown also
      // hides '1099' when Business Mode is on.
      logPersonalPayday: (entry) => {
        const amount = parseFloat(entry.amount) || 0;

        let bizEnabled = false;
        try {
          bizEnabled = !!useBusinessStore.getState().bizSettings?.enabled;
        } catch {
          bizEnabled = false;
        }
        const incomeType = bizEnabled
          ? 'w2'
          : (entry.incomeType || 'w2');

        let reserved = 0;
        let rate = 0;
        if (incomeType === '1099' && amount > 0) {
          rate = personalReserveRate(get().taxReserve);
          reserved = personalReserveTarget({ amount, incomeType }, rate);
        }

        // WHY: the reserve and its source payday must remain one accounting
        // fact. The sourceIncomeId lets edit/delete append an exact balancing
        // adjustment instead of leaving money behind or guessing by label.
        const record = {
          ...entry,
          id: entry.id || uid(),
          amount,
          incomeType,
          taxReserveAmount: reserved,
          taxReserveRateApplied: incomeType === '1099' ? rate : null,
        };
        set(state => ({ oneOffIncomes: [...state.oneOffIncomes, record] }));

        if (reserved > 0) {
          get().addTaxReserve(reserved, `1099: ${entry.name || 'payday'}`, {
            date: entry.date,
            sourceIncomeId: record.id,
            reserveSourceKind: 'personal-1099',
          });
        }

        return { amount, reserved, incomeType };
      },

      // ── Per-month overrides for recurring bills (edit-this-instance) ──
      // key: `${billId}:${YYYY-MM}` → { amount?, skipped?, paidForMonth? }
      recurringOverrides: {},
      setRecurringOverride: (billId, monthKey, override) => set(state => ({
        recurringOverrides: {
          ...state.recurringOverrides,
          [`${billId}:${monthKey}`]: { ...state.recurringOverrides[`${billId}:${monthKey}`], ...override },
        },
      })),
      clearRecurringOverride: (billId, monthKey) => set(state => {
        const next = { ...state.recurringOverrides };
        delete next[`${billId}:${monthKey}`];
        return { recurringOverrides: next };
      }),

      // ── Cash on hand (wallet balance) ──
      cashOnHand: 0,
      cashLedger: [],
      setCashOnHand: (cashOnHand) => set({ cashOnHand: Math.max(0, cashOnHand) }),
      adjustCashOnHand: (delta, meta = {}) => set(state => {
        const newBalance = Math.max(0, (state.cashOnHand || 0) + delta);
        const entry = {
          id: uid(),
          delta,
          balance: newBalance,
          date: meta.date || todayStr(),
          note: meta.note || '',
        };
        return {
          cashOnHand: newBalance,
          cashLedger: [...state.cashLedger, entry],
        };
      }),

      // ── Net Worth ──
      nw: { assets: [], debts: [] },
      setNetWorth: (nw) => set({ nw }),
      addAsset: (asset) => set(state => ({
        nw: { ...state.nw, assets: [...state.nw.assets, asset] },
      })),
      updateAsset: (id, updates) => set(state => ({
        nw: { ...state.nw, assets: state.nw.assets.map(a => a.id === id ? { ...a, ...updates } : a) },
      })),
      removeAsset: (id) => set(state => ({
        nw: { ...state.nw, assets: state.nw.assets.filter(a => a.id !== id) },
      })),
      addDebt: (debt) => set(state => ({
        nw: { ...state.nw, debts: [...state.nw.debts, debt] },
      })),
      removeDebt: (id) => set(state => ({
        nw: { ...state.nw, debts: state.nw.debts.filter(d => d.id !== id) },
      })),

      // ── Mortgage ──
      mortgage: null,
      setMortgage: (mortgage) => set({ mortgage }),

      // ── Personal Debts ──
      debts: [],
      setDebts: (debts) => set({ debts }),

      // ── Auto-categorization Rules ──
      rules: [],
      setRules: (rules) => set({ rules }),
      addRule: (rule) => set(state => ({ rules: [...state.rules, rule] })),
      removeRule: (id) => set(state => ({ rules: state.rules.filter(r => r.id !== id) })),

      // ── Budget Strategy ──
      strategy: '50/30/20',
      setStrategy: (strategy) => set({ strategy }),

      // ── Budget Rollovers ──
      rolloverCats: {},
      setRolloverCats: (rolloverCats) => set({ rolloverCats }),
      rollovers: {},
      setRollovers: (rollovers) => set({ rollovers }),

      // ── Credit Scores ──
      creditScores: [],
      setCreditScores: (creditScores) => set({ creditScores }),
      addCreditScore: (entry) => set(state => ({ creditScores: [...state.creditScores, entry] })),

      // ── Paycheck Planner State ──
      paycheckAllocations: {},
      setPaycheckAllocations: (paycheckAllocations) => set({ paycheckAllocations }),
      setPaycheckAllocation: (key, value) => set(state => ({
        paycheckAllocations: { ...state.paycheckAllocations, [key]: value },
      })),
      paycheckCustomItems: [],
      setPaycheckCustomItems: (paycheckCustomItems) => set({ paycheckCustomItems }),

      // ── Savings Buckets ──
      // Flexible envelope-style savings containers. Shape:
      //   { id, name, icon, color, target, balance }
      // Promoted from localStorage (`cb8:buckets`) into this store in v2 so
      // business allocations (businessStore.logBizIncome) can deposit into
      // specific buckets reactively without a second source of truth.
      buckets: [],
      setBuckets: (buckets) => set({ buckets }),
      addBucket: (bucket) => set(state => ({ buckets: [...state.buckets, bucket] })),
      updateBucket: (id, updates) => set(state => ({
        buckets: state.buckets.map(b => b.id === id ? { ...b, ...updates } : b),
      })),
      removeBucket: (id) => set(state => ({
        buckets: state.buckets.filter(b => b.id !== id),
      })),
      // Third param `_note` reserved for future transaction-log support.
      depositToBucket: (id, amount, _note) => set(state => ({
        buckets: state.buckets.map(b => b.id === id ? {
          ...b,
          // Uncapped: overflow is legal (oversave is fine). This differs from
          // the legacy localStorage BucketsTab which capped at target.
          balance: (parseFloat(b.balance) || 0) + (parseFloat(amount) || 0),
        } : b),
      })),
      withdrawFromBucket: (id, amount, _note) => set(state => ({
        buckets: state.buckets.map(b => b.id === id ? {
          ...b,
          balance: Math.max(0, (parseFloat(b.balance) || 0) - (parseFloat(amount) || 0)),
        } : b),
      })),

      // ── Tax Reserve ──
      taxReserve: { balance: 0, rate: 0.25, transactions: [] },
      addTaxReserve: (amount, source, meta = {}) => set(state => {
        const value = Math.max(0, roundMoney(amount));
        if (value <= 0) return {};
        return {
          taxReserve: {
            ...state.taxReserve,
            balance: roundMoney((parseFloat(state.taxReserve.balance) || 0) + value),
            transactions: [...state.taxReserve.transactions, {
              id: uid(),
              amount: value,
              source,
              date: meta.date || todayStr(),
              type: 'reserve',
              ...(meta.sourceIncomeId && { sourceIncomeId: meta.sourceIncomeId }),
              ...(meta.reserveSourceKind && { reserveSourceKind: meta.reserveSourceKind }),
            }],
          },
        };
      }),
      withdrawTaxReserve: (amount, note, targetPeriod = null) => {
        let withdrawn = 0;
        set(state => {
          const requested = Math.max(0, parseFloat(amount) || 0);
          const balance = Math.max(0, parseFloat(state.taxReserve.balance) || 0);
          withdrawn = Math.min(requested, balance);
          if (withdrawn <= 0) return state;
          const targetQuarter = Number(targetPeriod?.quarter);
          const targetYear = Number(targetPeriod?.year);
          const hasTargetPeriod = Number.isInteger(targetQuarter)
            && targetQuarter >= 1
            && targetQuarter <= 4
            && Number.isInteger(targetYear);
          return {
            taxReserve: {
              ...state.taxReserve,
              balance: balance - withdrawn,
              transactions: [...state.taxReserve.transactions, {
                id: Date.now().toString(36),
                amount: -withdrawn,
                source: note || 'Quarterly payment',
                date: todayStr(),
                type: 'payment',
                ...(hasTargetPeriod && {
                  paymentKind: 'estimated-tax',
                  targetQuarter,
                  targetYear,
                }),
              }],
            },
          };
        });
        return withdrawn;
      },
      setTaxReserveRate: (rate) => set(state => ({
        taxReserve: { ...state.taxReserve, rate },
      })),

      // ── Documents ──
      documents: [],
      addDocument: (doc) => set(state => ({ documents: [...state.documents, doc] })),
      updateDocument: (id, updates) => set(state => ({
        documents: state.documents.map(d => d.id === id ? { ...d, ...updates } : d),
      })),
      removeDocument: (id) => set(state => ({
        documents: state.documents.filter(d => d.id !== id),
      })),

      // ── Legacy Recovery (granular per-field import from cb8:* keys) ──
      // The old ClarityBudget.jsx monolith wrote finance data to
      // cb8:txns, cb8:incomes, etc. When the app switched to the
      // modular zustand stores (Phase 4), no migration was written —
      // so users' data under those legacy keys has been silently
      // ignored by the modular app. This action pulls the legacy
      // values (already synced from Supabase by syncFromCloud) into
      // the modern store, filling ONLY the fields that are currently
      // empty. A partially-populated store no longer blocks the
      // recovery of other fields — each field is checked and
      // imported independently, so incomes/goals/bills get restored
      // even when txns was restored first by an earlier run.
      importLegacyIfEmpty: () => {
        const s = get();
        const readLegacy = (key) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            return JSON.parse(raw);
          } catch { return null; }
        };

        const patch = {};

        // txns: object keyed by YYYY-MM → array of transactions
        if (Object.keys(s.txns || {}).length === 0) {
          const v = readLegacy('cb8:txns');
          if (v && typeof v === 'object' && !Array.isArray(v)) patch.txns = v;
        }
        // budgets: object keyed by YYYY-MM → category->amount map
        if (Object.keys(s.budgets || {}).length === 0) {
          const v = readLegacy('cb8:budgets');
          if (v && typeof v === 'object' && !Array.isArray(v)) patch.budgets = v;
        }
        // incomes: array of income sources
        if ((s.incomes?.length || 0) === 0) {
          const v = readLegacy('cb8:incomes');
          if (Array.isArray(v)) patch.incomes = v;
        }
        // goals: array of savings goals
        if ((s.goals?.length || 0) === 0) {
          const v = readLegacy('cb8:goals');
          if (Array.isArray(v)) patch.goals = v;
        }
        // cards: array of credit cards
        if ((s.cards?.length || 0) === 0) {
          const v = readLegacy('cb8:cards');
          if (Array.isArray(v)) patch.cards = v;
        }
        // recurring: array of recurring bills
        if ((s.recurring?.length || 0) === 0) {
          const v = readLegacy('cb8:recurring');
          if (Array.isArray(v)) patch.recurring = v;
        }
        // nw: { assets: [], debts: [] }
        if (((s.nw?.assets || []).length === 0) && ((s.nw?.debts || []).length === 0)) {
          const v = readLegacy('cb8:nw');
          if (v && (Array.isArray(v.assets) || Array.isArray(v.debts))) {
            patch.nw = { assets: v.assets || [], debts: v.debts || [] };
          }
        }
        // mortgage: single object
        if (!s.mortgage) {
          const v = readLegacy('cb8:mortgage');
          if (v && typeof v === 'object') patch.mortgage = v;
        }
        // debts: array of personal debts
        if ((s.debts?.length || 0) === 0) {
          const v = readLegacy('cb8:debts');
          if (Array.isArray(v)) patch.debts = v;
        }
        // rules: array of auto-categorization rules
        if ((s.rules?.length || 0) === 0) {
          const v = readLegacy('cb8:rules');
          if (Array.isArray(v)) patch.rules = v;
        }
        // rolloverCats / rollovers: objects
        if (Object.keys(s.rolloverCats || {}).length === 0) {
          const v = readLegacy('cb8:rollover_cats');
          if (v && typeof v === 'object') patch.rolloverCats = v;
        }
        if (Object.keys(s.rollovers || {}).length === 0) {
          const v = readLegacy('cb8:rollovers');
          if (v && typeof v === 'object') patch.rollovers = v;
        }

        if (Object.keys(patch).length === 0) {
          return { imported: false, reason: 'no-missing-fields-or-no-legacy-data' };
        }

        set(state => ({ ...state, ...patch }));
        return {
          imported: true,
          fields: Object.keys(patch),
          txnMonths: patch.txns ? Object.keys(patch.txns).length : 0,
          incomes: patch.incomes?.length || 0,
          goals: patch.goals?.length || 0,
          cards: patch.cards?.length || 0,
          recurring: patch.recurring?.length || 0,
          debts: patch.debts?.length || 0,
        };
      },
    }),
    {
      name: 'karat-finance',
      storage: karatStorage,
      version: 3,
      migrate: (persisted, fromVersion) => {
        // v1 → v2: promote legacy `cb8:buckets` localStorage to store.buckets
        if (fromVersion < 2) {
          if (!Array.isArray(persisted?.buckets)) {
            let legacy = [];
            try {
              const raw = localStorage.getItem('cb8:buckets');
              if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) legacy = parsed;
              }
            } catch { /* ignore — fall back to [] */ }
            persisted = { ...(persisted || {}), buckets: legacy };
          }
          // Leave cb8:buckets in place as rollback safety. It becomes
          // read-only after this migration — no code writes to it anymore.
        }
        // v2 → v3: backfill incomeType on incomes[] and oneOffIncomes[].
        // Idempotent via `=== undefined` guard. Does NOT fire addTaxReserve —
        // past money stays past. Flips userStore.incomeTypeBackfilled on success.
        if (fromVersion < 3) {
          let matched = 0;
          const nextIncomes = (persisted?.incomes || []).map(row => {
            if (row && row.incomeType === undefined) {
              const incomeType = classifyIncomeLabel(row.label);
              if (incomeType === '1099') matched++;
              return { ...row, incomeType };
            }
            return row;
          });
          const nextOneOffIncomes = (persisted?.oneOffIncomes || []).map(row => {
            if (row && row.incomeType === undefined) {
              const incomeType = classifyIncomeLabel(row.name);
              if (incomeType === '1099') matched++;
              return { ...row, incomeType };
            }
            return row;
          });
          persisted = { ...(persisted || {}), incomes: nextIncomes, oneOffIncomes: nextOneOffIncomes };

          // Flip userStore flags. Wrapped in try/catch in case userStore
          // isn't fully hydrated yet at migrate-time. If it fails, the
          // migration runs again next hydration — idempotent.
          try {
            const u = useUserStore.getState();
            if (typeof u.setIncomeTypeBackfilled === 'function') {
              u.setIncomeTypeBackfilled(true);
            }
            if (matched === 0 && typeof u.setIncomeTypeBackfillBannerDismissed === 'function') {
              u.setIncomeTypeBackfillBannerDismissed(true);
            }
          } catch { /* userStore not ready — retry next hydration */ }
        }
        return persisted;
      },
      partialize: (state) => ({
        txns: state.txns,
        budgets: state.budgets,
        incomes: state.incomes,
        cards: state.cards,
        goals: state.goals,
        recurring: state.recurring,
        equityGrants: state.equityGrants,
        activities: state.activities,
        oneOffIncomes: state.oneOffIncomes,
        recurringOverrides: state.recurringOverrides,
        cashOnHand: state.cashOnHand,
        cashLedger: state.cashLedger,
        nw: state.nw,
        mortgage: state.mortgage,
        debts: state.debts,
        rules: state.rules,
        strategy: state.strategy,
        rolloverCats: state.rolloverCats,
        rollovers: state.rollovers,
        creditScores: state.creditScores,
        paycheckAllocations: state.paycheckAllocations,
        paycheckCustomItems: state.paycheckCustomItems,
        taxReserve: state.taxReserve,
        buckets: state.buckets,
        documents: state.documents,
      }),
    }
  )
);
